import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useState, useCallback } from 'react';
import { placementLabel } from '@/lib/placementLabels';
import { adminApi } from '@/lib/adminApi';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Search, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight,
  Pause, Play, Eye, CheckCircle2, Sparkles, Rocket, Megaphone, Plus,
  Copy, Archive, BarChart3, Users,
} from 'lucide-react';
import PreviewTeamDialog from './sponsors/PreviewTeamDialog';
import CampaignWizard from './wizard/CampaignWizard';
import ProofOfValuePrint from './reports/ProofOfValuePrint';
import EditCopyDialog from './sponsors/EditCopyDialog';
import EditCampaignDialog from './sponsors/EditCampaignDialog';
import { CurrentDominicaWeatherBadge } from '@/components/CurrentDominicaWeatherBadge';
import { campaignServeStatus, type CampaignServeStatus } from '@/lib/campaignStatus';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Campaign {
  id: string;
  brand_name: string;
  slug?: string;
  placement: string;
  active: boolean;
  is_paused?: boolean;
  is_house_ad?: boolean;
  /** Only the preview team sees it in the app. */
  preview_only?: boolean;
  /** DB columns are starts_at / ends_at (TIMESTAMPTZ) */
  starts_at: string | null;
  ends_at: string | null;
  image_url: string | null;
  /** DB column is cta_url */
  cta_url: string | null;
  cpm_rate_xcd?: number | null;
  flat_fee_xcd?: number | null;
  invoice_status?: string | null;
  invoice_amount_xcd?: number | null;
  paid_at?: string | null;
  created_at: string;
}

const INVOICE_STYLES: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'bg-gray-500/10', text: 'text-gray-400' },
  invoiced: { bg: 'bg-blue-500/10', text: 'text-blue-400' },
  paid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  overdue: { bg: 'bg-red-500/10', text: 'text-red-400' },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-500' },
};

const SERVE_STATUS_BADGES: Record<
  CampaignServeStatus,
  { label: string; className: string; hint: string }
> = {
  live: {
    label: 'Live',
    className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    hint: 'Riders can see this campaign now.',
  },
  scheduled: {
    label: 'Scheduled',
    className: 'bg-blue-500/10 text-blue-400 border-0',
    hint: 'Starts on its start date.',
  },
  ended: {
    label: 'Ended',
    className: 'bg-gray-700/50 text-gray-400 border-gray-600',
    hint: 'The end date has passed. Riders no longer see it. Duplicate or edit the dates to run it again.',
  },
  paused: {
    label: 'Paused',
    className: 'bg-amber-500/10 text-amber-400 border-0',
    hint: 'Hidden from riders until you resume it.',
  },
  off: {
    label: 'Off',
    className: 'bg-gray-700/50 text-gray-400 border-gray-600',
    hint: 'Turned off in Edit.',
  },
};

function formatXCD(n: number | null | undefined): string {
  if (n == null || n === 0) return '–';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function Sponsors() {
  const { seesMoney, isManager } = useAdminAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editCopyCampaign, setEditCopyCampaign] = useState<Campaign | null>(null);
  const limit = 15;

  // Preview modal
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [reportCampaignId, setReportCampaignId] = useState<string | null>(null);
  const [previewTeamOpen, setPreviewTeamOpen] = useState(false);
  const [goPublicTarget, setGoPublicTarget] = useState<Campaign | null>(null);

  // Mark paid dialog
  const [markPaidCampaign, setMarkPaidCampaign] = useState<Campaign | null>(null);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);

  // Force push state
  const [forcePushCampaign, setForcePushCampaign] = useState<Campaign | null>(null);
  const [forcePushSubmitting, setForcePushSubmitting] = useState(false);
  const [forcePushAllOpen, setForcePushAllOpen] = useState(false);
  const [forcePushAllSubmitting, setForcePushAllSubmitting] = useState(false);
  const [activeCampaignCount, setActiveCampaignCount] = useState(0);

  // Tier-2 row actions
  const [archiveTarget, setArchiveTarget] = useState<Campaign | null>(null);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [duplicateSubmittingFor, setDuplicateSubmittingFor] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCampaigns({ page, limit, search: search || undefined });
      setCampaigns(res.data || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const openEdit = (c: Campaign) => {
    setEditingCampaign(c);
  };

  /** Soft-delete: keep the row, audit log, and historical events
   *  intact but make sure the campaign won't serve again. */
  const handleArchive = async () => {
    if (!archiveTarget) return;
    setArchiveSubmitting(true);
    try {
      await adminApi.updateCampaign(archiveTarget.id, {
        active: false,
        is_paused: true,
        ends_at: new Date().toISOString(),
      });
      toast.success(`Archived ${archiveTarget.brand_name}. It stays in the audit log but no longer serves.`);
      setArchiveTarget(null);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Failed to archive campaign');
    } finally {
      setArchiveSubmitting(false);
    }
  };

  /** Hard-delete: blows away the row + every ad_event + dismissal
   *  via the existing ON DELETE CASCADE. Irreversible. */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await adminApi.deleteCampaign(deleteTarget.id);
      toast.success(`${deleteTarget.brand_name} deleted permanently`);
      setDeleteTarget(null);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete campaign');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  /** Duplicate: server-side clone via admin_duplicate_campaign RPC.
   *  Returns a brand-new draft (paused + inactive) so the operator
   *  can tweak it before flipping it on. */
  const handleDuplicate = async (c: Campaign) => {
    setDuplicateSubmittingFor(c.id);
    try {
      const { data, error } = await supabase.rpc('admin_duplicate_campaign', {
        p_campaign_id: c.id,
      });
      if (error) throw error;
      const res = data as any;
      if (res?.ok === false) {
        toast.error(res.error || 'Duplicate failed');
        return;
      }
      toast.success(`Duplicated as ${res.new_slug}. Saved as a paused draft.`, {
        description: 'Open the new campaign from the list to edit it.',
        duration: 6000,
      });
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Duplicate failed');
    } finally {
      setDuplicateSubmittingFor(null);
    }
  };

  const handlePauseToggle = async (c: Campaign) => {
    const newPaused = !c.is_paused;
    try {
      await adminApi.updateCampaign(c.id, { is_paused: newPaused });
      toast.success(newPaused ? `${c.brand_name} paused` : `${c.brand_name} resumed`);
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, is_paused: newPaused } : x));
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle pause');
    }
  };

  const setPreviewOnly = async (c: Campaign, previewOnly: boolean) => {
    try {
      await adminApi.updateCampaign(c.id, { preview_only: previewOnly });
      toast.success(
        previewOnly
          ? `${c.brand_name} is now visible to the preview team only`
          : `${c.brand_name} is now visible to riders`,
      );
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, preview_only: previewOnly } : x));
    } catch (err: any) {
      toast.error(err.message || 'Failed to change who sees this campaign');
    }
  };

  const isCampaignPushable = (c: Campaign): boolean => {
    if (c.is_paused || !c.active) return false;
    const now = Date.now();
    if (c.starts_at && now < new Date(c.starts_at).getTime()) return false;
    if (c.ends_at && now > new Date(c.ends_at).getTime()) return false;
    return true;
  };

  const getPushDisabledReason = (c: Campaign): string | null => {
    if (c.is_paused) return 'Resume the campaign first';
    if (!c.active) return 'Activate the campaign first';
    const now = Date.now();
    if (c.starts_at && now < new Date(c.starts_at).getTime()) return 'Campaign has not started yet';
    if (c.ends_at && now > new Date(c.ends_at).getTime()) return 'Campaign has ended. Reschedule it first';
    return null;
  };

  const handleForcePush = async () => {
    if (!forcePushCampaign) return;
    setForcePushSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_force_push_campaign', {
        p_campaign_id: forcePushCampaign.id,
      });
      if (rpcErr) throw rpcErr;
      const result = data as any;
      if (result?.ok === false) {
        if (result.error === 'forbidden') {
          toast.error("You don't have permission. Ask a platform admin.");
        } else if (result.error === 'campaign_not_found') {
          toast.error('Campaign not found. Refreshing the list.');
        } else {
          toast.error(result.error || 'Force push failed');
        }
      } else if (result?.will_serve === false) {
        toast.warning(`Pushed, but campaign won't serve until you fix this: ${result.warning}`, { duration: 8000 });
      } else {
        toast.success(`Pushed ${forcePushCampaign.brand_name} and cleared ${result?.dismissals_wiped ?? 0} dismissals.`);
      }
      setForcePushCampaign(null);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Force push failed');
    } finally {
      setForcePushSubmitting(false);
    }
  };

  const handleForcePushAll = async () => {
    setForcePushAllSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_force_push_all_active');
      if (rpcErr) throw rpcErr;
      const result = data as any;
      if (result?.ok === false) {
        toast.error(result.error || 'Bulk force push failed');
      } else {
        toast.success(`Pushed ${result?.campaigns_pushed ?? 0} campaigns and cleared ${result?.dismissals_wiped ?? 0} dismissals.`);
      }
      setForcePushAllOpen(false);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Bulk force push failed');
    } finally {
      setForcePushAllSubmitting(false);
    }
  };

  const openForcePushAll = async () => {
    // Count active campaigns
    const count = campaigns.filter(c => c.active && !c.is_paused && !c.is_house_ad && isCampaignPushable(c)).length;
    setActiveCampaignCount(count);
    setForcePushAllOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!markPaidCampaign) return;
    setMarkPaidSubmitting(true);
    try {
      const amount = parseFloat(markPaidAmount) || markPaidCampaign.invoice_amount_xcd || null;
      const { data: rpcData, error: rpcErr } = await supabase.rpc('mark_campaign_invoice_paid', {
        p_campaign_id: markPaidCampaign.id,
        p_amount_xcd: amount,
      });
      if (rpcErr) throw rpcErr;
      const result = rpcData as any;
      if (result?.ok === false) throw new Error(result.error || 'Failed to mark paid');
      toast.success(`Invoice for ${markPaidCampaign.brand_name} marked as paid`);
      setMarkPaidCampaign(null);
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.message || 'Failed to mark as paid');
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {reportCampaignId && (
        <ProofOfValuePrint
          campaignId={reportCampaignId}
          onClose={() => setReportCampaignId(null)}
        />
      )}
      <PreviewTeamDialog open={previewTeamOpen} onOpenChange={setPreviewTeamOpen} />
      <Dialog open={goPublicTarget != null} onOpenChange={(o) => { if (!o) setGoPublicTarget(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Show {goPublicTarget?.brand_name} to riders?</DialogTitle>
            <DialogDescription className="text-gray-400">
              Every rider in the targeted area will start seeing this ad. Only do
              this once the business has agreed and the details are final.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGoPublicTarget(null)} className="text-gray-300">
              Keep as team preview
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={() => {
                const target = goPublicTarget;
                setGoPublicTarget(null);
                if (target) void setPreviewOnly(target, false);
              }}
            >
              Show to riders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Sponsors</h1>
          <p className="text-sm text-gray-400 mt-1">Manage ad campaigns and sponsorships</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Live read of what the ad resolver is seeing for Dominica
              right now. Operators can sanity-check that their
              weather-targeted campaigns are eligible to fire without
              digging into the mobile dev console. */}
          <CurrentDominicaWeatherBadge />
          <Button
            variant="outline"
            onClick={() => setPreviewTeamOpen(true)}
            className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
          >
            <Users className="w-4 h-4 mr-2" /> Preview team
          </Button>
          {isManager && (
            <Button variant="outline" onClick={openForcePushAll} className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500">
              <Megaphone className="w-4 h-4 mr-2" /> Force Push All Active
            </Button>
          )}
          <Button onClick={() => setWizardOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Sponsor
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          placeholder="Search by brand name..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="pl-9 bg-gray-900 border-gray-800 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Table */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 bg-gray-800/50 rounded" />
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No campaigns found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800 hover:bg-transparent">
                  <TableHead className="text-gray-400">Brand</TableHead>
                  <TableHead className="text-gray-400">Placement</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  {seesMoney && <TableHead className="text-gray-400">Bid</TableHead>}
                  {seesMoney && <TableHead className="text-gray-400">Invoice</TableHead>}
                  <TableHead className="text-gray-400">Dates</TableHead>
                  <TableHead className="text-gray-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => {
                  const invoiceStatus = c.invoice_status || 'pending';
                  const invStyle = INVOICE_STYLES[invoiceStatus] || INVOICE_STYLES.pending;
                  return (
                    <TableRow
                      key={c.id}
                      className={`border-gray-800 hover:bg-gray-800/30 ${c.is_paused ? 'opacity-50' : ''}`}
                    >
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          {c.brand_name}
                          {c.is_house_ad && (
                            <Badge variant="outline" className="text-[10px] border-gray-700 text-gray-500">House</Badge>
                          )}
                          {c.preview_only && (
                            <Badge
                              className="text-[10px] bg-violet-500/15 text-violet-300 border-0"
                              title="Only the preview team sees this in the app. Riders never do."
                            >
                              Team preview
                            </Badge>
                          )}
                        </div>
                        {c.slug && (
                          <p className="text-[11px] text-gray-500 font-mono mt-0.5">{c.slug}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs border-gray-700 text-gray-300">
                          {placementLabel(c.placement)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = SERVE_STATUS_BADGES[campaignServeStatus(c)];
                          return (
                            <Badge className={`${s.className} text-xs`} title={s.hint}>
                              {s.label}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      {seesMoney && <TableCell className="text-gray-300 text-sm tabular-nums">
                        {c.flat_fee_xcd
                          ? `${formatXCD(c.flat_fee_xcd)} flat`
                          : c.is_house_ad
                            ? <span className="text-gray-500">Free</span>
                            : c.cpm_rate_xcd
                              ? `${formatXCD(c.cpm_rate_xcd)} CPM`
                              : <span className="text-gray-500">–</span>
                        }
                      </TableCell>}
                      {seesMoney && <TableCell>
                        <Badge className={`${invStyle.bg} ${invStyle.text} border-0 text-xs`}>
                          {invoiceStatus}
                        </Badge>
                        {c.invoice_amount_xcd != null && c.invoice_amount_xcd > 0 && (
                          <p className="text-[11px] text-gray-500 mt-0.5 tabular-nums">{formatXCD(c.invoice_amount_xcd)}</p>
                        )}
                      </TableCell>}
                      <TableCell className="text-gray-400 text-sm">
                        {c.starts_at ? new Date(c.starts_at).toLocaleDateString() : '–'}
                        {' → '}
                        {c.ends_at ? new Date(c.ends_at).toLocaleDateString() : '∞'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-gray-400 hover:text-white w-8 h-8"
                                onClick={() => setReportCampaignId(c.id)}
                                aria-label={`Performance report for ${c.brand_name}`}
                              >
                                <BarChart3 className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Performance report</TooltipContent>
                          </Tooltip>
                          {/* Preview */}
                          {(c.image_url || c.cta_url) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white w-8 h-8" onClick={() => setPreviewCampaign(c)}>
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Preview creative</TooltipContent>
                            </Tooltip>
                          )}
                          {/* Pause/Resume */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-amber-400 w-8 h-8" onClick={() => handlePauseToggle(c)}>
                                {c.is_paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">{c.is_paused ? 'Resume' : 'Pause'}</TooltipContent>
                          </Tooltip>
                          {/* Team preview on/off. Pins mark the business itself,
                              so they can't be limited to the team. */}
                          {c.placement !== 'map_pin_featured' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={`w-8 h-8 ${c.preview_only ? 'text-violet-400 hover:text-violet-300' : 'text-gray-400 hover:text-violet-400'}`}
                                  onClick={() => (c.preview_only ? setGoPublicTarget(c) : void setPreviewOnly(c, true))}
                                  aria-label={c.preview_only ? `Show ${c.brand_name} to riders` : `Limit ${c.brand_name} to the preview team`}
                                >
                                  <Users className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
                                {c.preview_only ? 'Team preview: click to show to riders' : 'Limit to the preview team'}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {/* Mark Paid */}
                          {seesMoney && ['invoiced', 'overdue'].includes(invoiceStatus) && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-emerald-400 hover:text-emerald-300 w-8 h-8"
                                  onClick={() => {
                                    setMarkPaidCampaign(c);
                                    setMarkPaidAmount(String(c.invoice_amount_xcd || 0));
                                  }}
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Mark as paid</TooltipContent>
                            </Tooltip>
                          )}
                          {/* Force Push */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`w-8 h-8 ${isCampaignPushable(c) ? 'text-amber-400 hover:text-amber-300' : 'text-gray-600 cursor-not-allowed'}`}
                                disabled={!isCampaignPushable(c)}
                                onClick={() => setForcePushCampaign(c)}
                              >
                                <Rocket className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">
                              {getPushDisabledReason(c) || 'Force Push'}
                            </TooltipContent>
                          </Tooltip>
                          {/* Edit Copy */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-emerald-400 w-8 h-8" onClick={() => setEditCopyCampaign(c)}>
                                <Sparkles className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit copy</TooltipContent>
                          </Tooltip>
                          {/* Edit */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white w-8 h-8" onClick={() => openEdit(c)}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Edit (all tabs)</TooltipContent>
                          </Tooltip>
                          {/* Duplicate */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-gray-400 hover:text-emerald-400 w-8 h-8"
                                disabled={duplicateSubmittingFor === c.id}
                                onClick={() => handleDuplicate(c)}
                              >
                                {duplicateSubmittingFor === c.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Copy className="w-3.5 h-3.5" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Duplicate as new paused draft</TooltipContent>
                          </Tooltip>
                          {isManager && (
                            <>
                              {/* Archive */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-gray-400 hover:text-amber-300 w-8 h-8"
                                    onClick={() => setArchiveTarget(c)}
                                    aria-label={`Archive ${c.brand_name}`}
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Archive (safe, keeps the row and history)</TooltipContent>
                              </Tooltip>
                              {/* Delete forever */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-gray-400 hover:text-red-400 w-8 h-8"
                                    onClick={() => setDeleteTarget(c)}
                                    aria-label={`Delete ${c.brand_name} forever`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-gray-800 border-gray-700 text-xs">Delete forever (irreversible)</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{total} campaigns total</p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-gray-400">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="text-gray-400">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Campaign — full tabbed editor */}
      <EditCampaignDialog
        campaign={editingCampaign}
        open={!!editingCampaign}
        onOpenChange={(o) => { if (!o) setEditingCampaign(null); }}
        onUpdated={fetchCampaigns}
      />

      {/* Archive (soft-delete) confirmation */}
      <Dialog open={!!archiveTarget} onOpenChange={(o) => { if (!o) setArchiveTarget(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Archive <span className="text-amber-400">{archiveTarget?.brand_name}</span>?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Sets the campaign to inactive and paused, and ends it now, so it
              stops serving immediately. Nothing is deleted: the row,
              ad_events history, and audit log all stay intact.
              <br /><br />
              To bring it back later, reopen Edit, turn Active on, turn Paused
              off, and set a new future End date. (An archived campaign's end
              date is in the past, so flipping Active alone will not make it
              serve again until you extend the end date.)
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveTarget(null)} className="text-gray-400" disabled={archiveSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleArchive} disabled={archiveSubmitting} className="bg-amber-600 hover:bg-amber-500 text-white">
              {archiveSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-forever confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="bg-gray-900 border-red-900/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Delete <span className="text-red-400">{deleteTarget?.brand_name}</span> forever?
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This will permanently remove the campaign row plus every
              impression, click, dismissal, and audit log link. <strong className="text-red-300">This cannot be undone.</strong>
              {' '}If you only want to stop the campaign serving, use Archive instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-gray-400" disabled={deleteSubmitting}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!deleteTarget) return;
                setArchiveTarget(deleteTarget);
                setDeleteTarget(null);
              }}
              disabled={deleteSubmitting}
              className="border-amber-700 text-amber-300 hover:bg-amber-500/10"
            >
              Archive instead
            </Button>
            <Button onClick={handleDelete} disabled={deleteSubmitting} className="bg-red-600 hover:bg-red-500 text-white">
              {deleteSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!previewCampaign} onOpenChange={(o) => { if (!o) setPreviewCampaign(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Creative Preview: {previewCampaign?.brand_name}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {previewCampaign?.placement.replace(/_/g, ' ')} • {previewCampaign?.slug}
            </DialogDescription>
          </DialogHeader>
          {previewCampaign?.image_url ? (
            <div className="relative bg-gray-950 rounded-lg overflow-hidden border border-gray-800">
              {/* Phone frame for mobile placements */}
              <div className="mx-auto max-w-[375px] bg-black rounded-2xl p-2">
                <div className="bg-gray-900 rounded-xl overflow-hidden">
                  {/* Simulated app header */}
                  <div className="h-10 bg-gray-800 flex items-center px-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 mr-2" />
                    <span className="text-xs text-gray-400">H-Plate App</span>
                  </div>
                  {/* Ad creative */}
                  <div className={`relative ${previewCampaign.placement === 'interstitial' ? 'aspect-[9/16]' : 'aspect-[16/9]'}`}>
                    <img
                      src={previewCampaign.image_url}
                      alt={previewCampaign.brand_name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-black/60 text-[10px] text-white px-1.5 py-0.5 rounded">
                      AD
                    </div>
                  </div>
                  {/* Simulated app content */}
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-gray-800 rounded w-3/4" />
                    <div className="h-3 bg-gray-800 rounded w-1/2" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">No image URL set</div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewCampaign(null)} className="text-gray-400">
              Close
            </Button>
            {previewCampaign?.cta_url && (
              <Button
                variant="outline"
                className="border-gray-700 text-gray-300"
                onClick={() => window.open(previewCampaign.cta_url!, '_blank')}
              >
                Open Click URL
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={!!markPaidCampaign} onOpenChange={(o) => { if (!o) setMarkPaidCampaign(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription className="text-gray-400">
              Confirm payment for {markPaidCampaign?.brand_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Amount Collected (XCD)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={markPaidAmount}
                onChange={(e) => setMarkPaidAmount(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500">
                Invoice amount: {formatXCD(markPaidCampaign?.invoice_amount_xcd)}. Override if the collected amount differs.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMarkPaidCampaign(null)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleMarkPaid}
              disabled={markPaidSubmitting || !markPaidAmount}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {markPaidSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Push Single Campaign Modal */}
      <Dialog open={!!forcePushCampaign} onOpenChange={(o) => { if (!o) setForcePushCampaign(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Force-push <span className="text-emerald-400">{forcePushCampaign?.brand_name}</span>?</DialogTitle>
            <DialogDescription className="text-gray-400">
              This wipes every passenger / driver's dismissal of this campaign and tells the live app to refresh on every device within seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {forcePushCampaign?.slug && (
              <p className="text-xs text-gray-500 font-mono">{forcePushCampaign.slug}</p>
            )}
            <div className="bg-gray-800/50 rounded-lg p-3 text-sm text-gray-300 space-y-1">
              <p className="font-medium text-gray-200">Will reach users who:</p>
              <ul className="list-disc list-inside text-gray-400 text-xs space-y-0.5">
                <li>Have not yet hit today's frequency cap</li>
                <li>Are inside the campaign's audience / corridor / hour / weather rules</li>
                <li>Are running the app currently or open it next</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setForcePushCampaign(null)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleForcePush}
              disabled={forcePushSubmitting}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              {forcePushSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Rocket className="w-4 h-4 mr-2" />
              Force Push Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force Push All Active Modal */}
      <Dialog open={forcePushAllOpen} onOpenChange={setForcePushAllOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Force-push <span className="text-amber-400">all active campaigns</span>?</DialogTitle>
            <DialogDescription className="text-gray-400">
              Every paid campaign that is currently active (not paused, inside its date window, not a house ad) will get its dismissal cache wiped and will be forced to refresh on every running device.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm text-amber-300">
                This is the right move when you've just rolled out a fresh weekly buy, or are kicking off a major event (Carnival, Creole Festival).
              </p>
            </div>
            <p className="text-sm text-gray-300">
              Active campaigns this will affect: <span className="font-bold text-white">{activeCampaignCount}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setForcePushAllOpen(false)} className="text-gray-400">
              Cancel
            </Button>
            <Button
              onClick={handleForcePushAll}
              disabled={forcePushAllSubmitting || activeCampaignCount === 0}
              className="bg-amber-600 hover:bg-amber-500 text-white"
            >
              {forcePushAllSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Megaphone className="w-4 h-4 mr-2" />
              Push All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Sponsor Wizard */}
      <CampaignWizard
        mode="sponsor"
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onPublished={() => fetchCampaigns()}
      />

      {/* Edit Copy Dialog */}
      {editCopyCampaign && (
        <EditCopyDialog
          campaign={editCopyCampaign}
          open={!!editCopyCampaign}
          onOpenChange={(o) => { if (!o) setEditCopyCampaign(null); }}
          onUpdated={() => fetchCampaigns()}
        />
      )}
    </div>
  );
}
